import { useEffect, useMemo, useRef } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@mesh.ai/rabbit';
import { PlainTextHeader } from 'Deer/components/HeaderComponents/PlainTextHeader';
import { getApplicableL2CompanyModules } from 'Deer/components/navigation/secondary/Company/CompanyModules';
import { useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { CompensationIntentDropdown } from 'Deer/intent/Compensation/components/CompensationIntentDropdown';
import {
  CompensationRootState,
  setIsCompensationRecommendationBottomSheetOpen,
} from 'Deer/intent/Compensation/components/slice/compensationDrawersSlice';
import { Layout } from 'Deer/intent/components/Layout/Layout';
import { AgGridReact } from 'ag-grid-react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useQuery } from 'components/hooks/useQuery';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { setSelectedView, useSelectedView } from 'reducers/viewsSlice';

import { MODULE_KEYS } from 'constants/ProjectConstants';
import { getPersistenceKey } from 'constants/helper';
import { sendClickedOnPreferencesTabEvent } from 'constants/mixpanelHelpers/genericEvents';

import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { CompensationAnalysisMessage } from './components/CompensationAnalysisMessage';
import { CompensationRecommendationBottomSheet } from './components/CompensationRecommendationBottomSheet';
import { PerformanceRatingScatterChart } from './components/PerformanceRatingScatterChart';
import PlanningGraph from './components/PlanningGraph';
import { PlanningGrid } from './components/PlanningGrid/PlanningGrid';
import { PlanningMetricCards } from './components/PlanningMetricCards';
import { PlanningTable } from './components/PlanningTable';
import { ProposeAISection } from './components/ProposeAISection';

// Define compensation planning tabs based on Figma design and requirements
const COMPENSATION_PLANNING_TABS = [
  {
    name: 'Propose',
    queryParamId: 'propose',
    count: 10,
  },
  {
    name: 'Approve',
    queryParamId: 'approve',
    count: 10,
  },
  {
    name: 'Publish',
    queryParamId: 'publish',
    count: 3,
  },
];

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cohort: any;
  headerLabel: string;
}

const MyCompanyCompensationPlanning = ({ cohort, headerLabel }: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { activeTabId } = useQuery();
  const { moduleName } = useOrganisationSettings();
  const applicableL2CompanyModules = getApplicableL2CompanyModules(moduleName) ?? [];
  const gridRef = useRef<AgGridReact>(null);
  const { l1Module } = useRouteToNavLevels();
  const isManagerRoute = l1Module === 'team';
  const isCompensationRecommendationBottomSheetOpen = useSelector(
    (state: CompensationRootState) => state.compensationDrawers.isCompensationRecommendationBottomSheetOpen
  );
  // Filter tabs based on route - remove publish tab for manager routes
  const availableTabs = useMemo(
    () => COMPENSATION_PLANNING_TABS.filter((tab) => !(isManagerRoute && tab.queryParamId === 'publish')),
    [isManagerRoute]
  );

  const cohortDetails = useSelectedView();
  const viewId = cohortDetails?.id;
  const gridStatePersistenceKey = useMemo(() => {
    const uniqueId = 'MyCompanyCompensationPlanningGrid';

    return getPersistenceKey({
      moduleName: MODULE_KEYS.COMPENSATION,
      viewId,
      tabId: '',
      uniqueId,
    });
  }, [viewId]);

  const onTabChangeHandler = (newActiveTab: string) => {
    sendClickedOnPreferencesTabEvent({
      eventProperties: {
        activeTabId,
        source: 'compensation_planning',
      },
    });
    history.push({
      search: `?${new URLSearchParams({
        activeTabId: newActiveTab,
      }).toString()}`,
    });
  };

  useEffect(() => {
    dispatch(setSelectedView({ ...cohort, teamId: cohort.id }));
    return () => {
      dispatch(setSelectedView({}));
    };
  }, [cohort, dispatch]);

  // Set default tab if none is selected OR if current tab is not available for this route
  if (!activeTabId || !availableTabs.some((tab) => tab.queryParamId === activeTabId)) {
    onTabChangeHandler(availableTabs[0].queryParamId);
  }

  // Determine what components to show based on active tab
  const isProposedTab = activeTabId === 'propose';
  const showGraphAndAIMessage = activeTabId === 'approve' || activeTabId === 'publish';
  const showMetricCards = true; // All tabs show metric cards
  const showMasterTable = activeTabId === 'publish';

  return (
    <Layout
      Filters={
        <CompensationIntentDropdown
          breadcrumbs={{
            items: [
              {
                label: 'Company',
                to: applicableL2CompanyModules[0]?.value,
              },
            ],
            page: {
              label: 'Compensation',
              to: '',
            },
          }}
        />
      }
    >
      {headerLabel && <PlainTextHeader>{headerLabel}</PlainTextHeader>}

      <Tabs
        variant="secondary"
        value={activeTabId as string}
        onValueChange={onTabChangeHandler}
        style={{ marginTop: '-2px' }}
      >
        <TabsList>
          {availableTabs.map(({ name, queryParamId, count = 0 }) => (
            <TabsTrigger key={queryParamId} value={queryParamId}>
              {name} ({count})
            </TabsTrigger>
          ))}
        </TabsList>

        {availableTabs.map(({ queryParamId }) => (
          <TabsContent
            key={queryParamId}
            value={queryParamId}
            css={{ padding: '12px', background: 'white', height: 'calc(100vh - 175px)', overflowY: 'auto' }}
          >
            <Flex flexDirection="column" gap="8px" padding="8px" background=" #F5F7F9" borderRadius="8px">
              {/* Conditionally show AI section only for Propose tab */}
              {isProposedTab && (
                <Flex flexDirection="column" gap="12px" w="100%" bg="white" padding="12px" borderRadius="8px">
                  <TextBodyMedium bold>Merit Pay Alignment</TextBodyMedium>
                  <Flex gap="12px" w="100%">
                    <Box flex={1} bg="white" border="1px solid" borderColor="neutral.EBEFF3" borderRadius="8px">
                      <PerformanceRatingScatterChart />
                    </Box>
                    <Box flex={1}>
                      <ProposeAISection />
                    </Box>
                  </Flex>
                </Flex>
              )}

              {/* Conditionally show graph for Approve and Publish tabs */}
              {showGraphAndAIMessage && (
                <Flex flexDirection="column" gap="12px" w="100%" bg="white" padding="12px" borderRadius="8px">
                  <TextBodyMedium bold>Budget Overview</TextBodyMedium>
                  <Flex gap="12px" w="100%">
                    <Box flex={1} bg="white" border="1px solid" borderColor="neutral.EBEFF3" borderRadius="8px">
                      <PlanningGraph activeTabId={activeTabId} />
                    </Box>
                    <Box flex={1}>
                      <CompensationAnalysisMessage />
                    </Box>
                  </Flex>
                </Flex>
              )}

              {/* Show metric cards for all tabs (will be filtered by tab in component) */}
              {showMetricCards && (
                <PlanningMetricCards
                  gridStatePersistenceKey={gridStatePersistenceKey}
                  activeTab={activeTabId as string}
                />
              )}

              {/* Show table for all tabs (will have different columns based on tab) */}
              {showMasterTable ? (
                <PlanningGrid />
              ) : (
                <PlanningTable
                  gridRef={gridRef}
                  gridStatePersistenceKey={gridStatePersistenceKey}
                  activeTab={activeTabId as string}
                />
              )}
            </Flex>
          </TabsContent>
        ))}
      </Tabs>
      <CompensationRecommendationBottomSheet
        isOpen={isCompensationRecommendationBottomSheetOpen}
        onClose={() => dispatch(setIsCompensationRecommendationBottomSheetOpen(false))}
      />
    </Layout>
  );
};

export default MyCompanyCompensationPlanning;
